import * as React from "react";
import * as models from "app/models/requireme";
import * as kea from "kea-react";
import { all, nullOrEmpty } from "keautils";
import * as kearx from "kea-regex";
import * as resources from "app/resources/index";


/**Valida un objeto de identificacion de un cliente*/
export async function validarIdentificacion<TDatos>(x: models.Identificacion | undefined, datosRep: TDatos, resource: resources.ResourceIdentificacionRepetido<TDatos>): Promise<kea.validation.ValidationRuleResult> {
    const err = "Por lo menos un valor es obligatorio";
    if (!x) return err;

    const nullOrEmpty = (x: string | null | undefined) => x == null || x == "";
    const datos = [x.Curp, x.Ine, x.Pasaporte, x.Rfc];
    if (all(datos, nullOrEmpty)) {
        return err;
    } else {
        if (x.TipoPersona == models.TipoPersona.Moral && !x.Rfc) {
            return "RFC obligatorio cuando la persona es moral";
        }
        else {
            if (await kea.validation.hasError(erroresValidarIdentificacion(x, datosRep, resource))) {
                return "Algun campo inválido";
            }
        }
        return true;
    }
}

export async function validarIdentificacionNoRepetidos<TDatos>(x: models.Identificacion | undefined): Promise<kea.validation.ValidationRuleResult> {
    const err = "Por lo menos un valor es obligatorio";
    if (!x) return err;

    const nullOrEmpty = (x: string | null | undefined) => x == null || x == "";
    const datos = [x.Curp, x.Ine, x.Pasaporte, x.Rfc];
    if (all(datos, nullOrEmpty)) {
        return err;
    } else {
        if (await kea.validation.hasError(erroresValidarIdentificacionNoRepetidos(x))) {
            return "Algun campo inválido";
        }
        return true;
    }
}

export const reglasValidacionIdentificacion: kea.validation.Rules<models.Identificacion> = {
    Rfc: (value, key) => {
        if (!value.Rfc) return true;

        const x = value;
        const valRfc = validarRFC(x.Rfc, x.TipoPersona);
        if (valRfc == "invalido") return "RFC inválido";
        return true;
    },
    TipoPersona: (value, key) => {
        if (value.TipoPersona == null) return " ";//"Requerido";
        const x = value;
        if (!nullOrEmpty(value.Rfc) && value.TipoPersona == null) return "Obligatorio si se establece RFC";
        const valRfc = validarRFC(x.Rfc, x.TipoPersona);
        if (valRfc == "personaError") return "No concuerda con RFC";
        return true;
    }
};

export const reglasValidacionRecpetor: kea.validation.Rules<models.ReceptorFactura> = {
    Rfc: (value, key) => {
        if (!value.Rfc) return true; 
        const x = value;
        const valRfc = validarRFC(x.Rfc, x.TipoPersona);
        if (valRfc == "invalido") return "RFC inválido";
        return true;
    },
    TipoPersona: (value, key) => {
       if (value.TipoPersona == null) return "Requerido";
        const x = value;
        if (!nullOrEmpty(value.Rfc) && value.TipoPersona == null) return "Obligatorio si se establece RFC";
        const valRfc = validarRFC(x.Rfc, x.TipoPersona);
        if (valRfc == "personaError") return "No concuerda con RFC"; 
        return true;
    }
};

/**Obtiene todos los errores de validación de una identificación de un cliente*/
export function erroresValidarIdentificacion<TDatos>(x: models.Identificacion | undefined, datos: TDatos, resource: resources.ResourceIdentificacionRepetido<TDatos>): kea.validation.Result<models.Identificacion> {
    if (!x) return {};
    const rules = reglasValidacionIdentificacion;

    const ruleRepetido = (campo: resources.CampoRepetidoIdentificacion) => async (value: models.Identificacion, key: keyof models.Identificacion) => {
        if (!nullOrEmpty(value[key] as string)) {
            const rep = await resource.RepetidoIdentificacion(value[key] as string, campo, datos);
            return rep ? "Duplicado" : true
        } else {
            return true;
        }
    }

    const repetidos: kea.validation.Rules<models.Identificacion> = {
        Curp: ruleRepetido("Curp"),
        Pasaporte: ruleRepetido("Pasaporte"),
        Ine: ruleRepetido("Ine")
    };

    const allRules = kea.validation.andRules(rules, repetidos);
    return kea.validation.getErrors(allRules, x);
}

/**Obtiene todos los errores de validación de una identificación de un cliente*/
export function erroresValidarIdentificacionNoRepetidos<TDatos>(x: models.Identificacion | undefined): kea.validation.Result<models.Identificacion> {
    if (!x) return {};
    const rules = reglasValidacionIdentificacion;
    return kea.validation.getErrors(rules, x);
}

const rfcRegex = (() => {
    const char = kearx.any(kearx.charEsp, /&/);
    const identFisica = kearx.nTimes(char, 4);
    const identMoral = kearx.nTimes(char, 3);

    const año = kearx.integerRange(0, 99);
    const mes = kearx.integerRange(1, 12);
    const dia = kearx.integerRange(1, 31);
    const homoclave = kearx.nTimes(kearx.alphanum, 3);

    const postfix = kearx.seq(año, mes, dia, homoclave);
    const fisica = kearx.exact(kearx.seq(identFisica, postfix));
    const moral = kearx.exact(kearx.seq(identMoral, postfix));

    return { fisica, moral };
})();
function matchRfc(rfc: string): models.TipoPersona | undefined {
    if (rfcRegex.fisica.test(rfc)) {
        return models.TipoPersona.Fisica
    } else if (rfcRegex.moral.test(rfc)) {
        return models.TipoPersona.Moral;
    }
    return undefined;
}

/**Valida un RFC. Devuelve el tipo de error o undefined si el rfc esta bien 
 * @param persona Tipo de persona que se desea validar o indefinido si no nos interesa validar por tipo de persona
*/
export function validarRFC(rfc: string, persona: models.TipoPersona | null): "invalido" | "personaError" | undefined {
    const tipoRfc = matchRfc(rfc);
    if (tipoRfc == undefined) {
        return "invalido";
    } else if (persona != undefined && tipoRfc != persona) {
        return "personaError";
    } else {
        return undefined;
    }
}

/**Valida un email */
export function validarEmail(email) {
    var re = /^(?:(?:.+@.+\.[A-Za-z]+);?)+$/;
    return re.test(String(email).toLowerCase());
}


// WEBPACK FOOTER //
// ./app/logic/cliente.tsx